/**
 * Hook for applying version diffs as Word Track Changes.
 *
 * Strategy: Enable changeTrackingMode = "TrackAll", then insert/delete text
 * via the Range API. Word automatically records these as tracked changes.
 *
 * Requires WordApi 1.4 for changeTrackingMode.
 * Microsoft 365 only — does NOT work on Office 2019/2021.
 */

import { useState, useCallback, useRef } from 'react'
import { devLog } from '../services/devLog'
import type { DiffBlock } from '../services/negotiationsApi'

export interface ApplyResult {
  applied: number
  skipped: number
  errors: string[]
}

function isTrackChangesSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported('WordApi', '1.4')
  } catch {
    return false
  }
}

/**
 * Normalize text for comparison — trim and collapse whitespace.
 * Word paragraphs may have trailing spaces or different whitespace than backend text.
 */
function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

export function useTrackChanges() {
  const [isApplying, setIsApplying] = useState(false)
  const isSupported = useRef(isTrackChangesSupported()).current

  const applyRedlineAsTrackChanges = useCallback(async (
    blocks: DiffBlock[],
  ): Promise<ApplyResult> => {
    const result: ApplyResult = { applied: 0, skipped: 0, errors: [] }

    if (!isSupported) {
      result.errors.push('WordApi 1.4 not supported')
      return result
    }

    // Filter to only actionable blocks (skip 'equal')
    const actionableBlocks = blocks.filter((b) => b.type !== 'equal')
    if (actionableBlocks.length === 0) {
      return result
    }

    setIsApplying(true)
    try {
      await Word.run(async (context) => {
        // Save original tracking mode to restore later
        const doc = context.document
        doc.load('changeTrackingMode')
        await context.sync()

        const originalMode = doc.changeTrackingMode

        // Enable Track Changes
        doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll
        await context.sync()

        try {
          // Load all paragraphs
          const paragraphs = doc.body.paragraphs
          paragraphs.load('text')
          await context.sync()

          const totalParagraphs = paragraphs.items.length

          // Sort blocks by OLD paragraph index DESCENDING — process from bottom up
          // so that inserts/deletes don't shift indices for remaining blocks.
          // We use paragraph_number_old because the user has the OLD version open in Word.
          const sorted = [...actionableBlocks].sort((a, b) => {
            const idxA = a.paragraph_number_old ?? a.paragraph_number_new ?? 0
            const idxB = b.paragraph_number_old ?? b.paragraph_number_new ?? 0
            return idxB - idxA
          })

          for (const block of sorted) {
            try {
              switch (block.type) {
                case 'replace': {
                  // Use paragraph_number_old — the paragraph exists in the OLD doc the user has open
                  const idx = (block.paragraph_number_old ?? 0) - 1
                  if (idx < 0 || idx >= totalParagraphs) {
                    result.skipped++
                    break
                  }
                  const para = paragraphs.items[idx]
                  // Verify text matches before replacing
                  const paraText = normalizeText(para.text)
                  const expectedText = normalizeText(block.old_text)
                  if (paraText !== expectedText) {
                    devLog.warn('TrackChanges', `Paragraph ${idx + 1} text mismatch, skipping`)
                    result.skipped++
                    break
                  }
                  // Replace entire paragraph text — Word records as tracked change
                  const range = para.getRange()
                  range.insertText(block.new_text, Word.InsertLocation.replace)
                  result.applied++
                  break
                }

                case 'delete': {
                  // Use paragraph_number_old — the paragraph to delete exists in OLD doc
                  const idx = (block.paragraph_number_old ?? 0) - 1
                  if (idx < 0 || idx >= totalParagraphs) {
                    result.skipped++
                    break
                  }
                  const para = paragraphs.items[idx]
                  const paraText = normalizeText(para.text)
                  const expectedText = normalizeText(block.old_text)
                  if (paraText !== expectedText) {
                    devLog.warn('TrackChanges', `Delete paragraph ${idx + 1} text mismatch, skipping`)
                    result.skipped++
                    break
                  }
                  // Delete paragraph — Word records as tracked deletion
                  para.delete()
                  result.applied++
                  break
                }

                case 'insert': {
                  // For inserts, paragraph_number_old is null (new paragraph).
                  // Use paragraph_number_new to determine position relative to surrounding paragraphs.
                  // Since we process bottom-up, earlier paragraphs still have correct indices.
                  const newIdx = (block.paragraph_number_new ?? 0) - 1
                  if (newIdx < 0) {
                    result.skipped++
                    break
                  }
                  // Insert at start of body, or after the preceding paragraph in the OLD doc
                  if (newIdx === 0) {
                    doc.body.insertParagraph(block.new_text, Word.InsertLocation.start)
                  } else {
                    // The paragraph before this insert in the NEW version corresponds
                    // to an OLD paragraph. Use Math.min to clamp within bounds.
                    const prevIdx = Math.min(newIdx - 1, totalParagraphs - 1)
                    paragraphs.items[prevIdx].insertParagraph(
                      block.new_text,
                      Word.InsertLocation.after,
                    )
                  }
                  result.applied++
                  break
                }
              }
            } catch (blockErr) {
              const msg = blockErr instanceof Error ? blockErr.message : String(blockErr)
              devLog.warn('TrackChanges', `Block error: ${msg}`)
              result.errors.push(msg)
              result.skipped++
            }
          }

          await context.sync()
        } finally {
          // Restore original tracking mode
          doc.changeTrackingMode = originalMode
          await context.sync()
        }
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      devLog.warn('TrackChanges', `Apply failed: ${msg}`)
      result.errors.push(msg)
    } finally {
      setIsApplying(false)
    }

    devLog.info('TrackChanges', `Result: ${result.applied} applied, ${result.skipped} skipped, ${result.errors.length} errors`)
    return result
  }, [isSupported])

  return { applyRedlineAsTrackChanges, isApplying, isSupported }
}
